import React from 'react';
import { Box, Button, Card, Chip, Stack, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Bloco from './componentes/Bloco';
import CoberturaChip from '../components/CoberturaChip';
import { estadoDosDados, CAMPOS_DO_BALANCO_COMPLETO } from './apresentacao';
import { CAMPOS_DO_BALANCO_CURTO } from '../diagnostico/catalogo';
import Colunas from './componentes/Colunas';
import TituloDaTela from './componentes/TituloDaTela';
import { LARGURA_DE_LEITURA } from './layout';

/**
 * Tela Dados — de onde vem o número, e o que ainda falta.
 *
 * Ela é uma tela que se VISITA, e por isso não apaga nada: a versão anterior
 * desta navegação zerava o dashboard ao entrar na importação, o que fazia
 * "conferir o que já lancei" custar um recarregamento inteiro.
 *
 * O estado de completude é persistente. O painel de lacunas do wizard só
 * aparecia durante o preenchimento e sumia depois — justamente quando o usuário
 * já tinha o dashboard na frente e podia decidir se valia a pena completar.
 */
/**
 * Um caminho de entrada — e por que ele é ou não o seu.
 *
 * A tela oferecia três cartões do mesmo tamanho, com o mesmo peso, e deixava a
 * escolha inteira para quem acabou de chegar e ainda não sabe o que o produto
 * faz. Três opções equivalentes não são liberdade: são a decisão empurrada para
 * quem tem menos informação para tomá-la. Agora um caminho é recomendado, cada um
 * diz quanto tempo custa e o que entrega, e o indisponível não disputa atenção
 * com os que funcionam.
 */
const Caminho = ({ icone, titulo, paraQuem, tempo, entrega, acao, onClick, recomendado }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      borderColor: recomendado ? 'primary.main' : 'divider',
      // O ESTADO "INDISPONÍVEL" SAIU DAQUI, e com ele a borda tracejada e o
      // ícone apagado. Os três caminhos existem: dois se percorrem sozinho, o
      // terceiro se contrata. Um cartão desenhado como desligado ensinava que
      // aquilo não é para o cliente — e era justamente o mais caro dos três.
      transition: 'border-color 180ms ease-out, transform 180ms ease-out',
      ...(onClick
        ? { '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' } }
        : {})
    }}
  >
    {recomendado && (
      <Chip size="small" color="primary" label="Comece por aqui" sx={{ position: 'absolute', top: 16, right: 16 }} />
    )}
    <Box sx={{ color: 'primary.main', mb: 1.5 }}>{icone}</Box>
    <Typography variant="h6" component="h2">{titulo}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{paraQuem}</Typography>
    {tempo && (
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
        <ScheduleOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.secondary">{tempo}</Typography>
      </Stack>
    )}
    {entrega && <Typography variant="body2" sx={{ mt: 1.5, maxWidth: '40ch' }}>{entrega}</Typography>}
    <Box sx={{ flexGrow: 1 }} />
    <Button
      variant={recomendado ? 'contained' : 'outlined'}
      endIcon={<ArrowForwardIcon />}
      onClick={onClick}
      sx={{ mt: 2.5, alignSelf: 'flex-start', minHeight: 44 }}
    >
      {acao}
    </Button>
  </Card>
);

const TelaDados = ({
  leitura, arquivos, autenticado, onImportar, onQuestionario, onFecharMes, onConexaoDireta
}) => {
  const estado = estadoDosDados(leitura, arquivos);

  return (
    <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
      <Box>
        <TituloDaTela
          titulo="Dados"
          descricao="De onde vem o número que vou olhar, e o que ainda falta para o diagnóstico ficar completo?"
        />

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          {/* O ROTULO AQUI E O CONTEUDO, NAO DECORACAO.
              Medido em 390px: "DRE em 7 meses · balanço pendente — 19
              indicadores bloq…" — o Chip do MUI trunca numa linha so, e o que
              some e justamente a parte que diz o que o usuario GANHA
              completando. Esta e a primeira frase da primeira tela; cortada,
              ela vira enfeite. Rotulo que quebra em mais linhas, e o chip
              cresce, como o Plano de Acao ja faz com o nome do indicador. */}
          <Chip
            label={estado.frase}
            color={estado.temDRE && estado.temBalanco ? 'success' : 'default'}
            variant="outlined"
            sx={{
              height: 'auto',
              maxWidth: '100%',
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                display: 'block',
                py: 0.5,
                lineHeight: 1.4
              }
            }}
          />
          <CoberturaChip cobertura={estado.cobertura} />
        </Stack>
      </Box>

      {/* O CAMINHO RECOMENDADO VEM PRIMEIRO NO CÓDIGO, e não só na tela.
          Empilhados no celular, os cartões saíam na ordem do código e o
          recomendado ficava em SEGUNDO, abaixo de "Importar planilha": quem
          chega novo lê primeiro o caminho que não é o dele, e o chip "Comece
          por aqui" aparece depois do começo.
          `order` no CSS resolveria só o que se VÊ — leitor de tela e navegação
          por teclado seguem o DOM, e deixar os dois em desacordo troca um
          defeito de leitura por outro. Por isso o questionário está primeiro
          aqui, e `order` só devolve ao desktop o arranjo de três colunas em
          esforço crescente. */}
      <Colunas colunas={3}>
        <Box sx={{ order: { xs: 1, md: 2 }, display: 'flex', '& > *': { width: '100%' } }}>
          <Caminho
            icone={<EditNoteOutlinedIcon fontSize="large" />}
            titulo="Questionário guiado"
            paraQuem="Para quem tem os números, mas não tem uma planilha pronta."
            tempo="Cerca de 10 minutos — o rascunho fica salvo"
            entrega="O sistema pergunta conta por conta, como um consultor faria, e o diagnóstico sai completo no fim."
            acao={estado.temDRE ? 'Lançar outro mês' : 'Preencher'}
            onClick={onQuestionario}
            // O chip "Comece por aqui" só faz sentido para quem ainda não lançou
            // nada; depois do primeiro DRE o caminho recomendado passa a ser
            // fechar o mês, e ele mora no cartão de baixo. Sem isso a tela
            // voltava a ser três cartões equivalentes — a escolha inteira
            // empurrada para quem tem menos informação para tomá-la.
            recomendado={!estado.temDRE}
          />
        </Box>
        <Box sx={{ order: { xs: 2, md: 1 }, display: 'flex', '& > *': { width: '100%' } }}>
          <Caminho
            icone={<CloudUploadOutlinedIcon fontSize="large" />}
            titulo="Importar planilha"
            paraQuem="Para quem já fecha o mês numa planilha de DRE e balanço."
            tempo="Menos de 1 minuto por arquivo"
            entrega="Um mês ou vinte e quatro: é o mesmo arquivo, com mais colunas. Você baixa a planilha de volta quando quiser conferir."
            acao="Importar"
            onClick={onImportar}
          />
        </Box>
        <Box sx={{ order: { xs: 3, md: 3 }, display: 'flex', '& > *': { width: '100%' } }}>
          {/* ESTE CARTÃO DIZIA "AINDA NÃO DISPONÍVEL", E ISSO ERA MEIA VERDADE.
              Não existe como botão que a pessoa aperta sozinha — e nunca vai
              existir, porque ligar-se ao banco do ERP de uma empresa é
              implantação, com acesso, escopo e responsabilidade. Existe como
              serviço, e é o plano Full Time. Anunciar como indisponível o item
              mais caro do catálogo era ensinar o cliente a não perguntar.

              O QUE A COPY NÃO PODE FAZER: prometer que isto liga sozinho ao
              assinar. Por isso a última frase diz "sob projeto", e o botão
              pede orçamento em vez de contratar. */}
          <Caminho
            icone={<StorageOutlinedIcon fontSize="large" />}
            titulo="Conexão direta"
            paraQuem="Para quem quer o consultor ligado ao sistema que a empresa já usa."
            entrega="Os números vêm da fonte, sem digitação e sem planilha, e os 39 sinais passam a ser reavaliados conforme o movimento entra — com aviso quando um deles fica crítico. É o plano Full Time, implantado sob projeto."
            acao="Solicitar orçamento"
            onClick={onConexaoDireta}
          />
        </Box>
      </Colunas>

      {autenticado && (
        <Bloco
          titulo="Fechar o mês"
          pergunta="Acrescentar um mês ao histórico, sem refazer o que já foi lançado."
          acao={
            <Button
              variant="contained"
              startIcon={<EventRepeatIcon />}
              onClick={onFecharMes}
              sx={{ minHeight: 44 }}
            >
              Fechar um mês
            </Button>
          }
          nota="Para corrigir um mês já lançado, escolha ele na mesma tela: o formulário abre preenchido com o que está lá e só o que você mudar é enviado. Corrigir é sempre liberado e não consome nada do plano — cobrar por corrigir o próprio dado puniria justamente quem está caprichando nele."
        >
          {estado.lacunas.length > 0 ? (
            <Typography variant="body2" sx={{ maxWidth: '65ch' }}>
              Faltam lançamentos em <strong>{estado.lacunas.join(', ')}</strong>. Enquanto o mês não
              existir, ele aparece como um buraco na série — o gráfico interrompe a linha e a tabela
              mostra um traço, em vez de fingir que a empresa faturou zero.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '65ch' }}>
              Nenhum mês faltando dentro do período já lançado.
            </Typography>
          )}
        </Bloco>
      )}

      {!estado.temBalanco && (
        <Bloco
          titulo="O que o balanço destrava"
          pergunta="Sem ele, caixa, ciclo e retorno não são calculados — e não são exibidos como zero."
        >
          <Typography variant="body2" sx={{ mb: 2, maxWidth: '65ch' }}>
            São dois degraus. Com <strong>{CAMPOS_DO_BALANCO_CURTO.length} campos</strong> (
            {CAMPOS_DO_BALANCO_CURTO.join(', ').toLowerCase()}) abrem liquidez e ciclo. Com os{' '}
            <strong>{CAMPOS_DO_BALANCO_COMPLETO.length} campos</strong> do balanço completo abrem
            também endividamento e retorno ao sócio.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {estado.bloqueados.map((nome) => (
              <Chip key={nome} label={nome} size="small" variant="outlined" />
            ))}
          </Box>
        </Bloco>
      )}
    </Stack>
  );
};

export default TelaDados;
